<template>
  <div
    v-if="wizardStep === 1 || company"
    ref="rootEl"
    class="onboarding-wizard-root"
    :class="{ 'onboarding-wizard-root--chat-step': wizardStep === 2 }"
  >
    <OnboardingLoadingOverlay
      :model-value="postBasicTransitionLoading"
      :title="$t('onboarding.wizard.preparing-data.title')"
      :hint="preparingDataStatusText"
    />
    <OnboardingLoadingOverlay
      :model-value="postOnboardingRedirectLoading"
      :title="$t('onboarding.wizard.redirecting.title')"
      :hint="$t('onboarding.wizard.redirecting.hint')"
    />
    <OnboardingLoadingOverlay
      :model-value="!initialStepResolved"
      :title="$t('onboarding.wizard.preparing-data.title')"
      :hint="''"
    />
    <v-container
      class="onboarding-wizard"
      :class="{
        'pa-0': smAndDown || wizardStep === 2,
        'onboarding-wizard--enter-data': wizardStep === 2,
        'onboarding-wizard--chat-only': wizardStep === 2 && !showFormColumn,
        'onboarding-wizard--split-form': wizardStep === 2 && showFormColumn && showAssistantChat,
        'onboarding-wizard--form-only': wizardStep === 2 && showFormColumn && !showAssistantChat,
      }"
      :fluid="wizardStep === 2"
    >
      <OnboardingBasicInfo
        v-if="wizardStep === 1 && initialStepResolved"
        ref="basicInfoRef"
        :initial-website="company?.links?.website || ''"
        @continue="handleBasicInfoContinue"
      />
      <OnboardingEnterDataStep
        v-else-if="wizardStep === 2"
        :form-data="formData"
        :company="company"
        :ai-captured-fields="aiCapturedFields"
        :is-us-onboarding="isUsOnboarding"
        :legal-consents="reviewConsents"
        :show-assistant-chat="showAssistantChat"
        :show-form-column="showFormColumn"
        :can-switch-to-assistant="canSwitchToAssistant"
        :continue-label="continueFromEnterDataLabel"
        @update:field="onFieldUpdate"
        @update:legal-consents="Object.assign(reviewConsents, $event)"
        @image-uploaded="onImageUploaded"
        @widget-integration="handleReviewWidgetIntegration"
        @back="wizardStep = 1"
        @continue="handleContinueToReview"
        @switch-to-manual="switchToManualOnboarding"
        @switch-to-assistant="switchToAssistantOnboarding"
      />
      <div
        v-else-if="wizardStep === billingWizardStep"
        class="onboarding-billing-step px-2 px-md-4"
      >
        <h2 class="text-h5 font-weight-bold mb-2">
          {{ $t('onboarding.steps.payment') }}
        </h2>
        <p class="text-body-2 text-medium-emphasis mb-6">
          {{ $t('onboarding.wizard.billing.subtitle') }}
        </p>
        <BillingData
          v-if="billingDataProps"
          v-bind="billingDataProps"
          :prefill-contact="billingPrefillContact"
          is-onboarding
          hide-secondary-actions
          @nextStep="handleBillingStepNav"
        />
      </div>
      <div
        v-else-if="hasGuideStep && wizardStep === guideWizardStep"
        class="onboarding-guide-step px-2 px-md-4"
      >
        <h2 class="text-h5 font-weight-bold mb-2">
          {{ $t('onboarding.steps.guide') }}
        </h2>
        <p class="text-body-2 text-medium-emphasis mb-6">
          {{ $t('onboarding.wizard.guide.subtitle') }}
        </p>
        <Landingpages
          v-if="landingpageDataProps"
          :count-of-guides="countOfGuides"
          v-bind="landingpageDataProps"
          hide-cancel
          @nextStep="handleGuideStepNav"
        />
      </div>
      <OnboardingReview
        v-else-if="wizardStep === reviewWizardStep"
        :form-data="formData"
        :company="company"
        :is-us-onboarding="isUsOnboarding"
        @update:field="onFieldUpdate"
      >
        <template #submit-actions>
          <OnboardingReviewConsents
            :model-value="reviewConsents"
            :requires-agb-acceptance="requiresAgbAcceptance"
            :show-makler-mittelpunkt-offer="showMaklerMittelpunktOffer"
            :show-support-and-tips-hint="showSupportAndTipsHint"
            :review-acceptances-valid="reviewAcceptancesValid"
            :widget-initial-value="widgetInitialValue"
            :widget-integration-busy="widgetIntegrationBusy"
            :complete-loading="isSubmitting || postOnboardingRedirectLoading"
            @update:model-value="Object.assign(reviewConsents, $event)"
            @widget-integration="handleReviewWidgetIntegration"
            @back="wizardStep = stepBeforeReview"
            @complete="handleComplete"
          />
        </template>
      </OnboardingReview>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import './onboardingWizardLayout.css'

import { useQuery } from '@vue/apollo-composable'
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import { useBranding } from '@/composables/useBranding'
import featureNames from '@/lib/featureNames'
import GET_COMPONENT_SUBSCRIPTIONS_COUNT_BY_SLUG from '@/modules/billwerk/subscribe/queries/getComponentSubscriptionsCountBySlug.gql'
import BillingData from '@/modules/company/BillingData.vue'
import COMPANY from '@/modules/company/queries/Company.gql'
import type { TrackingPlugin } from '@/plugins/tracking'
import { useAiAssistantStore } from '@/stores/aiAssistant'

import OnboardingEnterDataStep from './components/OnboardingEnterDataStep.vue'
import OnboardingLoadingOverlay from './components/OnboardingLoadingOverlay.vue'
import OnboardingReviewConsents from './components/OnboardingReviewConsents.vue'
import { useOnboardingAssistant } from './composables/useOnboardingAssistant'
import { useOnboardingDataCompleteness } from './composables/useOnboardingDataCompleteness'
import { useOnboardingFeatures } from './composables/useOnboardingFeatures'
import { useOnboardingFieldPersistence } from './composables/useOnboardingFieldPersistence'
import { useOnboardingForm } from './composables/useOnboardingForm'
import { useOnboardingSubmit } from './composables/useOnboardingSubmit'
import { useOnboardingWizard } from './composables/useOnboardingWizard'
import Landingpages from './Landingpages.vue'
import {
  markOnboardingMilestoneTracked,
  readOnboardingProgress,
  readTrackedOnboardingMilestones,
  writeOnboardingProgress,
} from './lib/onboardingProgressStorage'
import OnboardingBasicInfo from './OnboardingBasicInfo.vue'
import OnboardingReview from './OnboardingReview.vue'

defineOptions({ name: 'Onboarding' })

const { t: tRaw } = useI18n()
const t = tRaw as (key: string, ...args: unknown[]) => string
const { smAndDown } = useDisplay()
const { isFeatureActive, isLogoEditable, isCommissionEditable } = useOnboardingFeatures()
const { platform } = useBranding()
const $auth = inject('auth') as { user?: { companyId?: string; email?: string } }
const tracking = inject<TrackingPlugin>('tracking')
const rootEl = ref<HTMLElement | null>(null)
const basicInfoRef = ref<{ stopLoading?: () => void } | null>(null)
const syncAiContext = ref(() => {})

const { result: companyResult, refetch: refetchCompanyQuery } = useQuery(COMPANY, () => ({
  id: $auth?.user?.companyId,
}))
const company = computed(() => companyResult.value?.company ?? null)

const wizard = useOnboardingWizard({
  t,
  hasMissingOnboardingData: () => completeness.hasMissingOnboardingData.value,
  getMissingOnboardingDataSummary: () => completeness.missingOnboardingDataSummary.value,
})
const {
  wizardStep,
  isAssistedOnboardingEnabled,
  activeEnterDataMode,
  showAssistantChat,
  showFormColumn,
  hasBillingStep,
  hasGuideStep,
  billingWizardStep,
  guideWizardStep,
  reviewWizardStep,
  stepBeforeReview,
  continueFromEnterDataLabel,
  wizardStepSlug,
  stepFromSlug,
  handleContinueToReview,
  handleBillingStepNav,
  handleGuideStepNav,
} = wizard

// --- Persist wizard position across page reloads ---------------------------
// `wizardStep` is in-memory only, so a refresh mid-onboarding used to drop the
// user back on the URL screen. Restore the last position from a per-company
// marker; wait for the company record to load before jumping to enter-data so
// the assistant + scrape resume with real values (not an empty form).
const companyId = computed(() => $auth?.user?.companyId || '')
const initialStepResolved = ref(true)
let pendingRestoreStep: number | null = null
const restoredMarker = readOnboardingProgress(companyId.value)
if (restoredMarker && restoredMarker.step >= 2) {
  const target = restoredMarker.slug ? stepFromSlug(restoredMarker.slug) : restoredMarker.step
  if (target >= 2) {
    pendingRestoreStep = target
    initialStepResolved.value = false
  }
}

function applyPendingRestore() {
  if (pendingRestoreStep == null || !company.value) return
  const target = pendingRestoreStep
  pendingRestoreStep = null
  populateFormFromCompany(company.value)
  wizardStep.value = target
  initialStepResolved.value = true
}

const form = useOnboardingForm({
  company,
  wizardStep,
  t,
  authEmail: computed(() => $auth?.user?.email || ''),
  companyIdFallback: computed(() => $auth?.user?.companyId || ''),
  syncAiContext,
})
const {
  formData,
  userEditedFields,
  aiCapturedFields,
  isUsOnboarding,
  handleFieldUpdate,
  populateFormFromCompany,
  flushPendingGenaisisPrefill,
  applyGenaisisSessionToForm,
  billingPrefillContact,
  billingDataProps,
  landingpageDataProps,
  countryValueForSubmit,
} = form

const completeness = useOnboardingDataCompleteness({
  formData,
  platform,
  isLogoEditable,
  isCommissionEditable,
  isUsOnboarding,
  t,
})

// Persist each field as the user edits it (debounced) so a refresh no longer
// loses in-progress data. Reuses the same input builders as the final submit.
const fieldPersistence = useOnboardingFieldPersistence({
  company,
  formData,
  isUsOnboarding,
  countryValueForSubmit,
})

function onFieldUpdate(field: string, value: string | boolean) {
  handleFieldUpdate(field, value)
  fieldPersistence.scheduleAutosave(field)
}

const skipGuideQuery = computed(
  () => !hasGuideStep.value || !isFeatureActive(featureNames.BILLWERK) || !$auth?.user?.companyId
)
const { result: subscribedResult } = useQuery(
  GET_COMPONENT_SUBSCRIPTIONS_COUNT_BY_SLUG,
  () => ({ input: { slug: 'Ratgeber', companyId: $auth?.user?.companyId } }),
  () => ({ enabled: !skipGuideQuery.value })
)
const countOfGuides = computed(() => (subscribedResult.value?.subcribedComponents || 0) + 1)

const assistant = useOnboardingAssistant({
  company,
  formData,
  wizardStep,
  wizardStepSlug,
  activeEnterDataMode,
  hasBillingStep,
  isAssistedOnboardingEnabled,
  isUsOnboarding,
  billingPrefillContact,
  countOfGuides,
  t,
  basicInfoRef,
  form,
  stepFromSlug,
  setWizardStep: (step) => {
    wizardStep.value = step
  },
  onContinueToBilling: () => handleContinueToReview(),
  syncAiContext,
  refetchCompany: () => refetchCompanyQuery(),
})
const {
  postBasicTransitionLoading,
  preparingDataStatusText,
  buildAndSyncAiContext,
  initOnboardingAssistantChat,
  handleBasicInfoContinue,
  onImageUploaded,
} = assistant

const submit = useOnboardingSubmit({
  company,
  formData,
  userEditedFields,
  aiCapturedFields,
  hasMissingOnboardingData: completeness.hasMissingOnboardingData,
  missingOnboardingDataSummary: completeness.missingOnboardingDataSummary,
  isUsOnboarding,
  t,
  refetchCompany: () => refetchCompanyQuery(),
  countryValueForSubmit,
  flushFieldAutosaves: () => fieldPersistence.flushPendingAutosaves(),
  getAutosavedContactId: () => fieldPersistence.createdContactId.value,
})
const {
  reviewConsents,
  isSubmitting,
  postOnboardingRedirectLoading,
  widgetIntegrationBusy,
  requiresAgbAcceptance,
  reviewAcceptancesValid,
  showSupportAndTipsHint,
  showMaklerMittelpunktOffer,
  widgetInitialValue,
  hydrateConsentsFromChat,
  handleReviewWidgetIntegration,
  handleComplete,
} = submit

function scrollWizardToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
  for (const el of [
    rootEl.value?.closest?.('.onboarding-route'),
    rootEl.value?.closest?.('.v-main'),
    document.querySelector('.v-main__wrap'),
  ].filter(Boolean) as HTMLElement[]) {
    el.scrollTop = 0
  }
}

const canSwitchToAssistant = computed(
  () => isAssistedOnboardingEnabled.value && showFormColumn.value
)

function switchToManualOnboarding() {
  useAiAssistantStore().switchToManualOnboarding()
  buildAndSyncAiContext()
}

function switchToAssistantOnboarding() {
  const aiStore = useAiAssistantStore()
  aiStore.switchToAssistantOnboarding()
  buildAndSyncAiContext()
  nextTick(() => {
    aiStore.open()
    void initOnboardingAssistantChat()
  })
}

// --- Heap tracking: enter-data step funnel -----------------------------
// Persisted per company (not just component-local state) — a page reload
// while resuming onboarding restores wizardStep from readOnboardingProgress
// above, and an `immediate` watcher on it would otherwise re-fire a milestone
// already reached in an earlier mount.
const trackedMilestones = readTrackedOnboardingMilestones(companyId.value)
// Milestones queued/in-flight but not yet accepted or failed. Prevents a
// rapid mode flip (e.g. chat -> form -> chat before the Heap callback for
// the first "chat_reached" attempt has run) from queuing the same event
// twice, since trackedMilestones alone wouldn't reflect it yet.
const pendingMilestones = new Set<string>()

function trackOnboardingMilestoneOnce(milestone: string, heapEventName: string) {
  if (trackedMilestones.has(milestone) || pendingMilestones.has(milestone)) return
  pendingMilestones.add(milestone)

  tracking?.heapEventReliably(
    heapEventName,
    undefined,
    () => {
      pendingMilestones.delete(milestone)
      trackedMilestones.add(milestone)
      markOnboardingMilestoneTracked(companyId.value, milestone)
    },
    () => {
      // Failed - don't mark as tracked, but also don't leave it stuck as
      // pending forever. The next time this milestone's condition becomes
      // true again it's free to retry.
      pendingMilestones.delete(milestone)
    }
  )
}

watch(
  () => (wizardStep.value === 2 ? (showAssistantChat.value ? 'chat' : 'form') : null),
  (mode) => {
    if (mode === 'chat') trackOnboardingMilestoneOnce('chat_reached', 'onboarding_chat_reached')
    else if (mode === 'form')
      trackOnboardingMilestoneOnce('form_reached', 'onboarding_form_reached')
  },
  { immediate: true }
)

watch(
  wizardStep,
  () => {
    const aiStore = useAiAssistantStore()
    // Persist position for reload restore. Skip while the initial step is still
    // being resolved so we don't clobber a saved marker with the default step 1.
    if (initialStepResolved.value) {
      writeOnboardingProgress(companyId.value, {
        step: wizardStep.value,
        slug: wizardStepSlug.value,
      })
    }
    aiStore.setOnboardingEmbeddedChatActive(wizardStep.value === 2)
    buildAndSyncAiContext()
    if (wizardStep.value === 2) {
      nextTick(() => {
        flushPendingGenaisisPrefill()
        aiStore.open()
        if (postBasicTransitionLoading.value) return
        void initOnboardingAssistantChat()
      })
    } else {
      aiStore.close()
    }
    if (wizardStep.value === reviewWizardStep.value) {
      const session = aiStore.lastOnboardingSession
      if (session) applyGenaisisSessionToForm(session)
      hydrateConsentsFromChat()
      aiStore.clearRequestedFieldFocus()
      trackOnboardingMilestoneOnce('review_reached', 'onboarding_review_reached')
    }
    nextTick(() => requestAnimationFrame(() => scrollWizardToTop()))
  },
  { immediate: true }
)

watch(
  company,
  (value) => {
    if (value) {
      if (pendingRestoreStep != null) applyPendingRestore()
      else populateFormFromCompany(value)
    }
    buildAndSyncAiContext()
  },
  { deep: true }
)

onMounted(() => {
  // The company query may have resolved before the watcher attached; apply now.
  if (pendingRestoreStep != null && company.value) applyPendingRestore()
  // Absolute fallback: never leave the user stuck behind the restore overlay if
  // the company record fails to load.
  if (!initialStepResolved.value) {
    window.setTimeout(() => {
      if (initialStepResolved.value) return
      if (pendingRestoreStep != null && company.value) applyPendingRestore()
      else {
        pendingRestoreStep = null
        initialStepResolved.value = true
      }
    }, 5000)
  }
})
</script>
